/**
 * ReportEditor — TipTap-based rich text editor for radiology report editing.
 *
 * Supports:
 * - Rich text editing (bold, italic, underline, lists, alignment)
 * - Save as Draft / Preliminary / Transcribed
 * - Sign report
 * - Addendum creation
 * - Macro insertion via MacroPicker
 * - Study locking (exclusive lock while editing)
 * - Auto-save drafts
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Save,
  CheckCircle,
  NoteAdd,
  Close,
  Undo,
  Redo,
  InsertDriveFile,
} from '@mui/icons-material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import type { Study, ReportBean, ReportStatus } from '../../api/types';
import { MacroPicker } from './MacroPicker';

// ── Helpers ──

/** Parse report text from backend JSON envelope */
function parseReportText(reportText?: string): string {
  if (!reportText) return '';
  try {
    const parsed = JSON.parse(reportText);
    return parsed.text ?? '';
  } catch {
    return reportText;
  }
}

/** Wrap plain text into backend report JSON envelope */
function wrapReportText(text: string): string {
  return JSON.stringify({ ver: 3, format: 'rawtext', text });
}

// ── Props ──

interface ReportEditorProps {
  /** Study to edit reports for */
  study: Study;
  /** Existing report to edit (null = create new) */
  report?: ReportBean | null;
  /** Called when editor is closed */
  onClose: () => void;
  /** Called after save/sign completes */
  onSaved?: () => void;
}

export const ReportEditor = ({ study, report, onClose, onSaved }: ReportEditorProps) => {
  const queryClient = useQueryClient();
  const lockKeyRef = useRef<string | null>(null);
  const autoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [macroPickerOpen, setMacroPickerOpen] = useState(false);
  const [addendumDialogOpen, setAddendumDialogOpen] = useState(false);
  const [addendumText, setAddendumText] = useState('');
  const [templateAnchorEl, setTemplateAnchorEl] = useState<HTMLElement | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  // ── Lock management ──
  const { data: lock, isLoading: lockLoading } = useQuery({
    queryKey: ['lock', study.objKey],
    queryFn: async () => {
      const result = await api.lockStudy(study.objKey);
      lockKeyRef.current = result.objKey;
      return result;
    },
    staleTime: Infinity,
    retry: false,
  });

  // Release lock on unmount
  useEffect(() => {
    return () => {
      if (lockKeyRef.current) {
        api.unlockStudy(lockKeyRef.current).catch(() => {});
        lockKeyRef.current = null;
      }
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current);
      }
    };
  }, []);

  // ── TipTap Editor ──
  const initialContent = parseReportText(report?.reportText);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['paragraph'],
      }),
      Placeholder.configure({
        placeholder: 'Begin typing your report…',
      }),
    ],
    content: initialContent || '<p></p>',
    editorProps: {
      attributes: {
        style:
          'min-height: 300px; padding: 16px; outline: none; font-family: monospace; font-size: 13px; line-height: 1.6;',
      },
    },
  });

  // ── Report template list ──
  const { data: templateList } = useQuery({
    queryKey: ['reportTemplateList'],
    queryFn: () => api.getReportTemplateList(),
    staleTime: 5 * 60_000,
    enabled: !!templateAnchorEl, // only fetch when menu opens
  });

  const handleTemplateSelect = useCallback(
    async (templateName: string) => {
      setTemplateAnchorEl(null);
      if (!editor) return;
      setTemplateLoading(true);
      try {
        const html = await api.getReportTemplate(templateName, undefined, study.objKey);
        if (html) {
          editor.chain().focus().setContent(html).run();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load template');
      } finally {
        setTemplateLoading(false);
      }
    },
    [editor, study.objKey],
  );

  // ── Save report ──
  const saveMutation = useMutation({
    mutationFn: async (status: ReportStatus) => {
      if (!editor) return;
      // Extract text content for raw storage
      const textContent = editor.getText();
      const wrappedText = wrapReportText(textContent);
      await api.editReport({
        stuKey: study.objKey,
        objKey: report?.objKey,
        text: wrappedText,
        status,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports(study.objKey) });
      onSaved?.();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // ── Sign report ──
  const signMutation = useMutation({
    mutationFn: async () => {
      if (!report?.objKey) {
        throw new Error('Report must be saved before signing');
      }
      // First save any unsaved changes as PRELIMINARY, capture returned objKey
      let currentObjKey = report.objKey;
      if (editor) {
        const textContent = editor.getText();
        const wrappedText = wrapReportText(textContent);
        const saved = await api.editReport({
          stuKey: study.objKey,
          objKey: report.objKey,
          text: wrappedText,
          status: 'PRELIMINARY',
        });
        if (saved?.objKey) currentObjKey = saved.objKey;
      }
      // Then sign using the (possibly updated) objKey
      await api.signReport(study.objKey, currentObjKey);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports(study.objKey) });
      onSaved?.();
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // ── Addendum ──
  const addendumMutation = useMutation({
    mutationFn: async (text: string) => {
      if (!report?.objKey) throw new Error('No report to add addendum to');
      await api.editAddendum({
        stuKey: study.objKey,
        objKey: report.objKey,
        index: -1,
        text,
        status: 'DRAFT',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports(study.objKey) });
      setAddendumDialogOpen(false);
      setAddendumText('');
      onSaved?.();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // ── Macro insertion ──
  const handleInsertMacro = useCallback(
    (text: string) => {
      if (editor) {
        editor.chain().focus().insertContent(text).run();
      }
      setMacroPickerOpen(false);
    },
    [editor],
  );

  // ── Auto-save (30s) ──
  useEffect(() => {
    if (!editor || !report?.objKey) return;
    const handler = () => {
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
      autoSaveRef.current = setTimeout(() => {
        saveMutation.mutate('DRAFT');
      }, 30_000);
    };
    editor.on('update', handler);
    return () => {
      editor.off('update', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, report?.objKey]);

  const handleSave = (status: ReportStatus) => {
    setSaving(true);
    setError(null);
    saveMutation.mutate(status, {
      onSettled: () => setSaving(false),
    });
  };

  if (lockLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress size={32} />
        <Typography sx={{ ml: 2 }}>Obtaining lock…</Typography>
      </Box>
    );
  }

  const isReadOnly = !lock;
  const isSigned = report?.status === 'SIGNED' || report?.status === 'AMENDED';

  return (
    <Paper
      elevation={2}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
          {report ? 'Edit Report' : 'New Report'} — {study.name} ({study.acc})
        </Typography>
        <Typography variant="caption" sx={{ mx: 2, color: 'text.secondary' }}>
          {report?.status ?? 'NEW'}
        </Typography>
        <IconButton size="small" onClick={onClose} title="Close editor">
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 1, mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* Toolbar */}
      <Toolbar variant="dense" sx={{ gap: 0.5, minHeight: 36, px: 1 }}>
        <Tooltip title="Bold">
          <IconButton
            size="small"
            onClick={() => editor?.chain().focus().toggleBold().run()}
            color={editor?.isActive('bold') ? 'primary' : 'default'}
          >
            <FormatBold fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton
            size="small"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            color={editor?.isActive('italic') ? 'primary' : 'default'}
          >
            <FormatItalic fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Underline">
          <IconButton
            size="small"
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            color={editor?.isActive('underline') ? 'primary' : 'default'}
          >
            <FormatUnderlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Bullet List">
          <IconButton
            size="small"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            color={editor?.isActive('bulletList') ? 'primary' : 'default'}
          >
            <FormatListBulleted fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Numbered List">
          <IconButton
            size="small"
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            color={editor?.isActive('orderedList') ? 'primary' : 'default'}
          >
            <FormatListNumbered fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Align Left">
          <IconButton
            size="small"
            onClick={() => editor?.chain().focus().setTextAlign('left').run()}
            color={editor?.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
          >
            <FormatAlignLeft fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Align Center">
          <IconButton
            size="small"
            onClick={() => editor?.chain().focus().setTextAlign('center').run()}
            color={editor?.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
          >
            <FormatAlignCenter fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Align Right">
          <IconButton
            size="small"
            onClick={() => editor?.chain().focus().setTextAlign('right').run()}
            color={editor?.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
          >
            <FormatAlignRight fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Undo">
          <IconButton size="small" onClick={() => editor?.chain().focus().undo().run()}>
            <Undo fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Redo">
          <IconButton size="small" onClick={() => editor?.chain().focus().redo().run()}>
            <Redo fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="text"
          startIcon={<InsertDriveFile />}
          onClick={(e) => setTemplateAnchorEl(e.currentTarget)}
          disabled={templateLoading || isReadOnly}
        >
          {templateLoading ? 'Loading…' : 'Templates'}
        </Button>
        <Menu
          anchorEl={templateAnchorEl}
          open={Boolean(templateAnchorEl)}
          onClose={() => setTemplateAnchorEl(null)}
          slotProps={{ paper: { sx: { maxHeight: 400, minWidth: 200 } } }}
        >
          {!templateList?.records?.length && (
            <MenuItem disabled>
              <ListItemText primary="No templates available" />
            </MenuItem>
          )}
          {templateList?.records?.map((name) => (
            <MenuItem key={name} onClick={() => handleTemplateSelect(name)}>
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </Menu>
        <Button size="small" variant="text" onClick={() => setMacroPickerOpen(true)}>
          Macros
        </Button>
      </Toolbar>

      <Divider />

      {/* Editor content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: 'background.paper',
          '& .tiptap': {
            minHeight: 300,
            p: 2,
            outline: 'none',
          },
          '& .tiptap p.is-editor-empty:first-child::before': {
            color: 'grey.400',
            content: 'attr(data-placeholder)',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      <Divider />

      {/* Action bar */}
      <Stack direction="row" spacing={1} sx={{ p: 1, bgcolor: 'background.default' }}>
        <ButtonGroup size="small" variant="outlined">
          <Button
            startIcon={<Save />}
            onClick={() => handleSave('DRAFT')}
            disabled={saving || isReadOnly}
          >
            Save Draft
          </Button>
          <Button
            onClick={() => handleSave('PRELIMINARY')}
            disabled={saving || isReadOnly}
          >
            Preliminary
          </Button>
          <Button
            onClick={() => handleSave('TRANSCRIBED')}
            disabled={saving || isReadOnly}
          >
            Transcribed
          </Button>
        </ButtonGroup>

        <Box sx={{ flex: 1 }} />

        {report && !isSigned && (
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={() => signMutation.mutate()}
            disabled={saving || signMutation.isPending || isReadOnly}
          >
            Sign Report
          </Button>
        )}
        {report && isSigned && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<NoteAdd />}
            onClick={() => setAddendumDialogOpen(true)}
          >
            Add Addendum
          </Button>
        )}
      </Stack>

      {/* Macro Picker Dialog */}
      <MacroPicker
        open={macroPickerOpen}
        siteKey={study.site}
        onClose={() => setMacroPickerOpen(false)}
        onInsert={handleInsertMacro}
      />

      {/* Addendum Dialog */}
      <Dialog open={addendumDialogOpen} onClose={() => setAddendumDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Addendum</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            minRows={6}
            fullWidth
            label="Addendum text"
            value={addendumText}
            onChange={(e) => setAddendumText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddendumDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => addendumMutation.mutate(addendumText)}
            disabled={!addendumText.trim() || addendumMutation.isPending}
          >
            Save Addendum
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
